import { clampMove, type MapData, type Vec2 } from "@agency/shared";
import type Phaser from "phaser";
import { net } from "../../net/NetClient.js";
import { AvatarView } from "./AvatarView.js";

// Slightly under the 50 ms target so frame-time jitter doesn't push sends to
// every 4th frame (≈15 Hz at 60 fps). Stays below the server's MOVE_MSG_MAX_HZ.
const SEND_INTERVAL_MS = 45;
/** Comfortably above the server's minimum gap (1000 / MOVE_MSG_MAX_HZ ≈ 33 ms). */
const STOP_MIN_GAP_MS = 40;
const STOP_RETRY_MS = 250;

/** The local, client-authoritative player (validated server-side). */
export class LocalPlayer {
  readonly view: AvatarView;
  pos: Vec2;
  private lastSendAt = 0;
  private seq = 0;
  private lastSentMoving = false;
  /** Last position the server knows about (sent by us or snapped by it). */
  private lastSentPos: Vec2;

  constructor(scene: Phaser.Scene, avatarId: string, nickname: string, pos: Vec2) {
    this.pos = { ...pos };
    this.lastSentPos = { ...pos };
    this.view = new AvatarView(scene, pos.x, pos.y, avatarId, nickname, true);
  }

  /** Move along `dir` (unnormalized), sliding against the map collision grid. */
  move(map: MapData, dir: Vec2, speedPxS: number, dtMs: number): void {
    const len = Math.hypot(dir.x, dir.y);
    if (len === 0) return;
    const step = speedPxS * (dtMs / 1000);
    const desired = {
      x: this.pos.x + (dir.x / len) * step,
      y: this.pos.y + (dir.y / len) * step,
    };
    this.pos = clampMove(map, this.pos, desired);
    this.view.setPosition(this.pos.x, this.pos.y);
  }

  snapTo(x: number, y: number): void {
    this.pos = { x, y };
    this.lastSentPos = { x, y };
    this.view.setPosition(x, y);
  }

  /**
   * Send our position at ~20 Hz while moving, and a stop message when we stop.
   * The server drops messages closer together than 1/MOVE_MSG_MAX_HZ, so the
   * stop waits out that gap, and is re-sent if the server still has us moving.
   */
  report(dir: number, moving: boolean, now: number, serverMoving: boolean): void {
    const sinceLast = now - this.lastSendAt;
    if (moving) {
      if (sinceLast < SEND_INTERVAL_MS) return;
    } else if (this.lastSentMoving || this.hasUnsentPosition()) {
      // A short move can be fully throttled; still flush where we ended up.
      if (sinceLast < STOP_MIN_GAP_MS) return;
    } else if (!(serverMoving && sinceLast > STOP_RETRY_MS)) {
      return;
    }
    this.lastSendAt = now;
    this.lastSentMoving = moving;
    this.lastSentPos = { ...this.pos };
    net.send("move", { x: this.pos.x, y: this.pos.y, dir, moving, seq: this.seq++ });
  }

  private hasUnsentPosition(): boolean {
    return this.pos.x !== this.lastSentPos.x || this.pos.y !== this.lastSentPos.y;
  }

  destroy(): void {
    this.view.destroy();
  }
}
